import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  SlidersHorizontal,
  Download,
  Wand2,
  Palette,
  ShoppingBag,
  CreditCard,
  Check,
  Hand,
  Eye,
  ShieldCheck,
  Truck,
  Star,
  ChevronLeft,
  ChevronRight,
  Info,
} from "lucide-react";
import { getPricing, formatPrice } from "@/lib/orderApi";
import { getStoreShowcase } from "@/lib/storeApi";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import Logo from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import { useAuth } from "@/context/AuthContext";
import { FORMAT_EXAMPLES, THREAD_COLORS, BOARDS, boardHex } from "@/constants/formatExamples";

const STEPS = [
  { icon: Palette, title: "1. Personalize", desc: "Envie a sua foto (ou crie um padrão geométrico) e ajuste cores, formato e densidade — com otimização inteligente que destaca o motivo." },
  { icon: Eye, title: "2. Veja na sua parede", desc: "Pré-visualize a peça emoldurada e pendurada em casa, de graça, antes de decidir. Sem precisar de criar conta." },
  { icon: CreditCard, title: "3. Encomende e receba", desc: "Pagamento seguro pela Stripe. Produzimos a sua arte à mão, fio a fio, e entregamos em sua casa." },
];

// Fotografias de PEÇAS REAIS acabadas (o que o cliente recebe).
const REAL_PIECES = [
  {
    id: "retrato-frente",
    src: "/examples/reais/retrato-frente.jpg",
    title: "Retrato de frente",
    meta: "Círculo Ø 40 cm · fio preto · placa branca",
  },
  {
    id: "retrato-perfil",
    src: "/examples/reais/retrato-perfil.jpg",
    title: "Retrato de perfil",
    meta: "Círculo Ø 40 cm · fio preto · placa branca",
  },
  {
    id: "geometrica-cor",
    src: "/examples/reais/geometrica-cor.jpg",
    title: "Geométrica a cores",
    meta: "Quadrado 50 × 50 cm · 5 cores · placa preta",
  },
];

// Os exemplos de formatos são arte REAL gerada pelo nosso motor
// (src/constants/formatExamples.js + /examples/formats/*.png).

const FRAME_BG = {
  wood: "linear-gradient(135deg, #e8d0a6, #c8a36a 45%, #8a6433)",
  black: "linear-gradient(135deg, #4a4a52, #26262a 45%, #0d0d10)",
};

const TRUST = [
  { icon: Hand, title: "Feito à mão", desc: "Cada peça é montada fio a fio, à mão." },
  { icon: Eye, title: "Pré-visualização grátis", desc: "Veja antes de comprar, sem conta." },
  { icon: ShieldCheck, title: "Pagamento seguro", desc: "Checkout protegido pela Stripe." },
  { icon: Truck, title: "Entrega em casa", desc: "Recebe a obra pronta para pendurar." },
];

const features = [
  { icon: Wand2, title: "Otimização inteligente", desc: "O sistema deteta o motivo da sua foto, limpa o fundo e realça os tons — para um resultado nítido e impressionante." },
  { icon: SlidersHorizontal, title: "Controlo total", desc: "Pregos, número de linhas, espessura, opacidade, cores e formato — ajuste tudo em tempo real." },
  { icon: Sparkles, title: "Edição manual", desc: "Conecte os pregos você mesmo no modo manual e refine cada detalhe da composição." },
  { icon: Download, title: "Exportação completa", desc: "Baixe PNG, SVG, PDF de instruções e a lista numerada de pregos para montar à mão." },
];

const SHAPE_LABELS = { circle: "Círculo", square: "Quadrado", rectangle: "Retângulo", heart: "Coração" };
const shapeLabel = (id) => SHAPE_LABELS[id] || id || "";

// A framed product mockup in one of the available formats (circle/square/
// rectangle/heart) and finishes (wood/black). A ARTE é um PNG cinza+alfa
// gerado pelo nosso motor: é usada como MÁSCARA CSS, o que permite pintar o
// fio com a cor escolhida pelo cliente e trocar o fundo da placa ao vivo.
function FramedPiece({ src, shape, label, motif, frame = "wood", thread, board, delay = 0 }) {
  const round = shape === "circle";
  const heart = shape === "heart";
  const outer = round ? "rounded-full" : heart ? "" : "rounded-md";
  const innerR = round ? "rounded-full" : heart ? "" : "rounded-[3px]";
  const innerRing = heart ? "" : "ring-2 ring-black/10";
  const sizeCls = shape === "rectangle" ? "w-full aspect-[4/3]" : "w-full aspect-square";
  const clip = heart ? { clipPath: "url(#heartClip)" } : undefined;
  const mask = {
    backgroundColor: thread,
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="flex flex-col items-center"
      data-testid={`format-example-${shape}`}
    >
      <div className="w-full aspect-square flex items-center justify-center">
        <div
          data-no-invert
          className={`relative ${sizeCls} shadow-2xl ${outer} transition-transform duration-500 hover:scale-[1.03]`}
          style={{ padding: "14px", background: FRAME_BG[frame] || FRAME_BG.wood, ...clip }}
        >
          <div
            className={`w-full h-full overflow-hidden ${innerRing} ${innerR}`}
            style={{ background: board, ...clip }}
          >
            <div className="w-full h-full" style={mask} aria-label={`${label} — ${motif}`} />
          </div>
        </div>
      </div>
      <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#8B8B93]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B00]" /> {label}
      </span>
      <span className="text-[11px] text-[#5F5F66] mt-0.5">{motif}</span>
    </motion.div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const location = useLocation();
  const dest = "/criar";
  const [sizes, setSizes] = useState([]);
  const [thread, setThread] = useState(THREAD_COLORS[0]);
  const [board, setBoard] = useState(THREAD_COLORS[0].board);
  const [showcase, setShowcase] = useState([]);
  const showcaseRef = useRef(null);

  useEffect(() => {
    getPricing()
      .then((p) => setSizes(p.sizes || []))
      .catch(() => setSizes([]));
    getStoreShowcase(12)
      .then((items) => setShowcase(items || []))
      .catch(() => setShowcase([]));
  }, []);

  // Suporta navegação a /#galeria (a partir do menu "Exemplos" noutras páginas)
  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash) return;
    // Pequeno delay para garantir que a secção já foi renderizada
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [location.hash]);

  const scrollShowcase = (dir) => {
    const el = showcaseRef.current;
    if (!el) return;
    const step = Math.max(280, Math.round(el.clientWidth * 0.85));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white overflow-x-hidden">
      {/* Heart clip-path used by the Coração frame format */}
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <defs>
          <clipPath id="heartClip" clipPathUnits="objectBoundingBox">
            <path d="M0.5,0.99 C0.5,0.99 0.04,0.63 0.04,0.31 C0.04,0.13 0.2,0.04 0.34,0.10 C0.43,0.14 0.5,0.25 0.5,0.25 C0.5,0.25 0.57,0.14 0.66,0.10 C0.8,0.04 0.96,0.13 0.96,0.31 C0.96,0.63 0.5,0.99 0.5,0.99 Z" />
          </clipPath>
        </defs>
      </svg>
      {/* Nav — cabeçalho público único (mesmo em todas as páginas) */}
      <PublicHeader />

      {/* Hero */}
      <section className="relative pt-14 pb-20 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 border border-[#3F3F46] px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 bg-[#FF3B00] rounded-full animate-pulse" />
              <span className="text-xs text-[#8B8B93] font-mono-val uppercase tracking-widest">
                Feito à mão · pré-visualização grátis
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              A sua foto vira uma{" "}
              <span className="text-[#FF3B00]">obra de arte em fios</span> na sua parede.
            </h1>
            <p className="mt-6 text-[#8B8B93] text-base md:text-lg max-w-xl leading-relaxed">
              Personalize a sua peça no navegador, <span className="text-white">veja-a emoldurada na sua parede</span> antes
              de comprar e receba o quadro feito à mão, fio a fio, em casa. Sem complicação e sem precisar de criar conta.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to={dest}
                data-testid="hero-cta-btn"
                className="group inline-flex items-center gap-2 bg-[#FF3B00] hover:bg-[#E63500] text-black font-semibold px-6 py-3.5 transition-colors duration-200"
              >
                Criar a minha arte — grátis
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/exemplos"
                data-testid="hero-ver-exemplos"
                className="inline-flex items-center gap-2 border border-[#3F3F46] hover:border-white text-white px-6 py-3.5 transition-colors duration-200"
              >
                Ver exemplos
              </Link>
            </div>
            {/* trust chips */}
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#8B8B93]">
              {["Sem necessidade de conta", "Pré-visualização grátis", "Pagamento seguro", "Entrega ao domicílio"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-[#5BBF7B]" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Hero visual: peça REAL — retrato em fios (fotografia da peça acabada) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10">
              <img
                src={REAL_PIECES[0].src}
                alt="Retrato em string art — peça acabada"
                className="absolute inset-0 w-full h-full object-cover"
                fetchpriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              {/* floating chip */}
              <div data-no-invert className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-2 rounded-lg shadow-lg">
                <div className="flex items-center gap-1 text-[#1a1a1a]">
                  <Star className="w-3.5 h-3.5 text-[#FF7A00] fill-[#FF7A00]" />
                  <span className="text-[12px] font-semibold">Feito à mão, só para si</span>
                </div>
                <div className="text-[10px] text-[#555]">A sua foto → retrato em fios</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="px-6 md:px-12 pb-8">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222225] border border-[#222225]">
          {TRUST.map((t) => (
            <div key={t.title} className="bg-[#0D0D0F] p-5 flex items-start gap-3">
              <t.icon className="w-5 h-5 text-[#FF3B00] mt-0.5 shrink-0" strokeWidth={1.6} />
              <div>
                <div className="text-sm font-semibold">{t.title}</div>
                <div className="text-[11px] text-[#8B8B93] leading-snug mt-0.5">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Uma cor ou a cores — a escolha que mais muda a peça */}
      <section id="cores" className="px-6 md:px-12 py-16 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl">
              Escolha primeiro: uma cor ou a cores?
            </h2>
            <p className="text-[#8B8B93] text-sm max-w-sm">
              É a decisão que mais muda o resultado. Pode experimentar as duas no editor,
              sem compromisso.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                id: "mono",
                title: "Uma cor",
                desc: "Um único fio do princípio ao fim — preto, branco ou qualquer cor. Traço limpo, muito contraste, o clássico do string art.",
                colors: ["#101010", "#101010"],
                bg: "#FFFFFF",
                swatches: ["#101010", "#F5F5F0", "#C81E1E", "#1E4FC8", "#C9A227"],
              },
              {
                id: "color",
                title: "A cores",
                desc: "Vários fios de cor sobrepostos, como uma impressora: ciano, magenta, amarelo e preto constroem a imagem em camadas.",
                colors: ["#00A9E0", "#EC008C", "#FFE800", "#101010"],
                bg: "#FFFFFF",
                swatches: ["#00A9E0", "#EC008C", "#FFE800", "#101010"],
              },
            ].map((o) => (
              <div key={o.id} className="border border-[#222225] bg-[#0D0D0F] p-6 flex flex-col sm:flex-row gap-6">
                <div className="w-full sm:w-40 shrink-0 aspect-square border border-[#222225]">
                  <svg data-no-invert viewBox="0 0 60 60" className="w-full h-full" style={{ background: o.bg }}>
                    {Array.from({ length: 26 }).map((_, i) => {
                      const a1 = (i / 26) * Math.PI * 2;
                      const a2 = ((i * 9) / 26) * Math.PI * 2;
                      return (
                        <line
                          key={i}
                          x1={30 + 26 * Math.cos(a1)}
                          y1={30 + 26 * Math.sin(a1)}
                          x2={30 + 26 * Math.cos(a2)}
                          y2={30 + 26 * Math.sin(a2)}
                          stroke={o.colors[i % o.colors.length]}
                          strokeWidth="0.6"
                          opacity="0.85"
                        />
                      );
                    })}
                  </svg>
                </div>
                <div className="flex-1 flex flex-col">
                  <h3 className="font-display text-2xl">{o.title}</h3>
                  <p className="text-sm text-[#8B8B93] mt-2 leading-relaxed">{o.desc}</p>
                  <div className="flex gap-1.5 mt-4">
                    {o.swatches.map((c) => (
                      <span key={c} data-no-invert className="w-5 h-5 border border-white/20" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-auto pt-5">
                    <Link
                      to={`/personalizar?goal=${o.id}`}
                      data-testid={`landing-goal-${o.id}`}
                      className="bg-[#FF3B00] hover:bg-[#E63500] text-black text-sm font-medium px-5 py-3 transition-colors"
                    >
                      Criar com a minha foto
                    </Link>
                    <Link
                      to={`/loja?cor=${o.id}`}
                      className="border border-[#3F3F46] hover:border-white text-sm px-5 py-3 transition-colors"
                    >
                      Ver peças prontas
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Peças reais acabadas */}
      <section className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl">Peças reais, feitas fio a fio.</h2>
            <p className="text-[#8B8B93] text-sm max-w-sm">
              Retratos de pessoas e padrões a cores — fotografias das peças acabadas, tal como chegam a casa.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="real-pieces-grid">
            {REAL_PIECES.map((p, i) => (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`real-piece-${p.id}`}
                className="group"
              >
                <div className="relative aspect-square overflow-hidden rounded-xl ring-1 ring-black/10 shadow-xl">
                  <img
                    src={p.src}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <figcaption className="mt-3">
                  <div className="text-sm font-semibold">{p.title}</div>
                  <div className="text-[11px] text-[#8B8B93] mt-0.5">{p.meta}</div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Antes -> Depois (interactive, real generated string art) */}
      <section className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl">Veja a transformação: foto → arte em fios.</h2>
            <p className="text-[#8B8B93] text-sm max-w-sm">Arraste o cursor sobre as imagens. O resultado é <span className="text-white">gerado pelo nosso motor</span> a partir da fotografia — é exatamente o que vai receber.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <BeforeAfterSlider
              src="/examples/retrato-homem.jpg"
              afterSrc="/examples/retrato-homem-art.jpg"
              label="Retrato — arraste ↔ para revelar"
            />
            <BeforeAfterSlider
              src="/examples/retrato-mulher.jpg"
              afterSrc="/examples/retrato-mulher-art.jpg"
              label="Retrato — arraste ↔ para revelar"
            />
          </div>
          <div className="mt-10 text-center">
            <Link
              to={dest}
              className="group inline-flex items-center gap-2 bg-[#FF3B00] hover:bg-[#E63500] text-black font-semibold px-6 py-3.5 transition-colors"
            >
              Experimentar com a minha foto
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="font-display text-3xl md:text-4xl mb-3 max-w-2xl">Da sua foto à sua parede — em 3 passos.</h2>
          <p className="text-[#8B8B93] max-w-xl mb-12">É simples, rápido e pode experimentar agora mesmo, de graça.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-[#222225] bg-[#121214] p-7"
              >
                <s.icon className="w-7 h-7 text-[#FF3B00]" strokeWidth={1.5} />
                <h3 className="mt-5 text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-[#8B8B93] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Galeria: formatos reais + escolha da cor do fio */}
      <section id="galeria" className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl">Escolha o formato — e a cor do fio.</h2>
            <p className="text-[#8B8B93] text-sm max-w-sm">
              Círculo, quadrado, retângulo ou coração, em moldura de madeira ou preta.
              Todas as peças aqui em baixo foram <span className="text-white">geradas pelo nosso motor</span> —
              é exatamente este tipo de arte que produzimos, fio a fio.
            </p>
          </div>

          {/* Seletor de cor do fio e do fundo da placa */}
          <div className="border border-[#222225] bg-[#0D0D0F] p-5 mb-10 flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider text-[#8B8B93] mb-2.5">
                Cor do fio — <span className="text-white">{thread.label}</span>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="thread-color-picker">
                {THREAD_COLORS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`thread-color-${c.id}`}
                    title={c.label}
                    onClick={() => {
                      setThread(c);
                      setBoard(c.board);
                    }}
                    className={`w-9 h-9 border-2 transition-transform hover:scale-110 ${
                      thread.id === c.id ? "border-[#FF3B00] scale-110" : "border-white/20"
                    }`}
                  >
                    <span data-no-invert className="block w-full h-full" style={{ background: c.hex }} />
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:border-l lg:border-[#222225] lg:pl-6">
              <div className="text-[11px] uppercase tracking-wider text-[#8B8B93] mb-2.5">Fundo da placa</div>
              <div className="flex flex-wrap gap-2" data-testid="board-picker">
                {BOARDS.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    data-testid={`board-${b.id}`}
                    onClick={() => setBoard(b.id)}
                    className={`px-3 py-2 text-xs border transition-colors inline-flex items-center gap-2 ${
                      board === b.id ? "border-[#FF3B00] text-white" : "border-[#222225] text-[#8B8B93] hover:border-[#3F3F46]"
                    }`}
                  >
                    <span data-no-invert className="w-3.5 h-3.5 border border-white/20" style={{ background: b.hex }} />
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {FORMAT_EXAMPLES.map((p, i) => (
              <FramedPiece
                key={p.id}
                src={p.src}
                shape={p.shape}
                label={p.label}
                motif={p.motif}
                frame={p.frame}
                thread={thread.hex}
                board={boardHex(board)}
                delay={(i % 3) * 0.08}
              />
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to={dest}
              className="group inline-flex items-center gap-2 bg-[#FF3B00] hover:bg-[#E63500] text-black font-semibold px-6 py-3.5 transition-colors"
            >
              Criar a minha — é grátis pré-visualizar
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/loja?cor=color"
              className="inline-flex items-center gap-2 border border-[#3F3F46] hover:border-white px-6 py-3.5 transition-colors"
            >
              Ver peças a cores na loja
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="font-display text-3xl md:text-4xl mb-12 max-w-2xl">Tudo o que precisa para criar uma peça incrível.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222225] border border-[#222225]">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#121214] p-7 hover:bg-[#1E1E22] transition-colors duration-300"
              >
                <f.icon className="w-6 h-6 text-[#FF3B00]" strokeWidth={1.5} />
                <h3 className="mt-5 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-[#8B8B93] leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Escolha o tamanho + carrossel de peças da loja (curadas pelo admin) */}
      <section id="precos" className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl">Escolha o tamanho do seu quadro.</h2>
            <p className="text-[#8B8B93] text-sm max-w-sm">
              Cada peça é única, montada à mão com fios e pregos.
              O <Link to="/loja" className="text-white hover:text-[#FF3B00] transition-colors">preço final</Link> é definido pelo tamanho e pelo acabamento.
            </p>
          </div>

          {/* Cards de tamanhos — SEM preços (foco em características) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sizes.map((s) => (
              <div
                key={s.id}
                data-testid={`size-${s.id}`}
                className={`relative border p-8 flex flex-col ${
                  s.id === "medium" ? "border-[#FF3B00] bg-[#FF3B00]/5" : "border-[#222225] bg-[#121214]"
                }`}
              >
                {s.id === "medium" && (
                  <span className="absolute top-0 right-0 bg-[#FF3B00] text-black text-[11px] font-medium px-2 py-1">
                    Mais escolhido
                  </span>
                )}
                <h3 className="text-xl font-semibold">{s.label}</h3>
                <p className="text-[#8B8B93] text-sm mt-1">Diâmetro/lado ~ {s.dimension}</p>
                <ul className="mt-6 space-y-2 text-sm text-[#8B8B93] flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#5BBF7B]" /> Arte 100% personalizada</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#5BBF7B]" /> Feita à mão, fio a fio</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#5BBF7B]" /> Pronta para pendurar</li>
                </ul>
                <Link
                  to={dest}
                  className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 transition-colors ${
                    s.id === "medium"
                      ? "bg-[#FF3B00] hover:bg-[#E63500] text-black font-medium"
                      : "border border-[#3F3F46] hover:border-[#FF3B00] hover:text-[#FF3B00]"
                  }`}
                >
                  Personalizar este tamanho <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          {/* Carrossel de peças curadas — cross-sell dos exemplos para a loja */}
          {showcase.length > 0 && (
            <div className="mt-16" data-testid="landing-showcase">
              <div className="flex items-end justify-between gap-3 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#FF3B00]">Selecionadas da loja</div>
                  <h3 className="font-display text-2xl md:text-3xl mt-2">Peças que pode encomendar já.</h3>
                  <p className="text-[#8B8B93] text-sm mt-1 max-w-xl">
                    Curadas manualmente pela nossa equipa — cada uma pronta a enviar em poucos dias.
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Anterior"
                    data-testid="showcase-prev"
                    onClick={() => scrollShowcase(-1)}
                    className="w-9 h-9 border border-[#222225] hover:border-[#FF3B00] text-[#C9C9CF] hover:text-white transition-colors inline-flex items-center justify-center"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Seguinte"
                    data-testid="showcase-next"
                    onClick={() => scrollShowcase(1)}
                    className="w-9 h-9 border border-[#222225] hover:border-[#FF3B00] text-[#C9C9CF] hover:text-white transition-colors inline-flex items-center justify-center"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div
                ref={showcaseRef}
                data-testid="showcase-track"
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-6 px-6 md:mx-0 md:px-0 custom-scrollbar"
              >
                {showcase.map((p) => (
                  <Link
                    key={p.id}
                    to={`/loja#produto-${p.id}`}
                    data-testid={`showcase-item-${p.id}`}
                    className="snap-start shrink-0 w-64 md:w-72 border border-[#222225] bg-[#121214] hover:border-[#FF3B00] transition-colors group"
                  >
                    <div className="aspect-square bg-[#0c0c0e] overflow-hidden">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt={p.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#3F3F46]">
                          <ShoppingBag className="w-10 h-10" strokeWidth={1.2} />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-[#8B8B93] mt-1 truncate">
                        {[p.dimension, p.shape && shapeLabel(p.shape)].filter(Boolean).join(" · ") || "Peça da loja"}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#FF3B00] group-hover:gap-2 transition-all">
                        Ver na loja <ArrowUpRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link
                  to="/loja"
                  className="inline-flex items-center gap-2 border border-[#3F3F46] hover:border-white px-6 py-3 text-sm transition-colors"
                >
                  Ver todo o catálogo <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 md:px-12 py-20 border-t border-[#222225]">
        <div className="max-w-[1400px] mx-auto border border-[#FF3B00]/40 bg-[#FF3B00]/5 p-10 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
            Pronto para ver a sua foto transformada em arte?
          </h2>
          <p className="mt-5 text-[#8B8B93] max-w-xl mx-auto">
            Demora menos de um minuto. Personalize, veja na sua parede e só encomende se adorar o resultado.
          </p>
          <Link
            to={dest}
            data-testid="final-cta-btn"
            className="group mt-8 inline-flex items-center gap-2 bg-[#FF3B00] hover:bg-[#E63500] text-black font-semibold px-8 py-4 text-lg transition-colors"
          >
            Criar a minha arte — grátis
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-10 border-t border-[#222225] text-sm text-[#8B8B93] flex flex-col md:flex-row items-center justify-between gap-3">
        <Logo full className="h-14 w-auto" />
        <span>Feito para quem quer uma peça única em casa · {new Date().getFullYear()}</span>
        <div className="flex items-center gap-4">
          <Link to="/loja" className="hover:text-white transition-colors">Loja</Link>
          <Link to="/exemplos" className="hover:text-white transition-colors">Exemplos</Link>
          <Link to="/sobre" className="hover:text-white transition-colors">Sobre</Link>
          <Link to="/consultar-encomenda" className="hover:text-white transition-colors">Consultar encomenda</Link>
          <Link to="/login" data-testid="footer-login-link" className="hover:text-white transition-colors">
            Entrar
          </Link>
          <Link to="/register" data-testid="footer-register-link" className="hover:text-white transition-colors">
            Criar conta
          </Link>
        </div>
      </footer>
    </div>
  );
}
